import { notification } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  REAMS_SUPPORT_REASON,
  useContactCustomerSupport,
} from '../../api/contactCustomerSupport';
import { trackTrialExpiredContactSales } from '../../helpers/analytics';

const CONTACT_SALES_SUBMITTED_STORAGE_KEY = 'trialExpiredContactSalesSubmitted';

const getSubmittedUsers = (): string[] => {
  try {
    return JSON.parse(
      localStorage.getItem(CONTACT_SALES_SUBMITTED_STORAGE_KEY) || '[]',
    );
  } catch {
    return [];
  }
};

const markSubmitted = (userId?: string) => {
  if (!userId) return;
  try {
    const users = getSubmittedUsers();
    if (!users.includes(userId)) {
      localStorage.setItem(
        CONTACT_SALES_SUBMITTED_STORAGE_KEY,
        JSON.stringify([...users, userId]),
      );
    }
  } catch {
    // Storage unavailable (private mode) - state still works for this session
  }
};

const hasSubmittedContactSales = (userId?: string) =>
  !!userId && getSubmittedUsers().includes(userId);

export const useContactSales = (source: string, userId?: string) => {
  const { t } = useTranslation('rentNavigator', { keyPrefix: 'trial' });
  const { t: tCommon } = useTranslation('common', {
    keyPrefix: 'contactSupportForm',
  });

  const [isSubmitted, setIsSubmitted] = useState<boolean>(() =>
    hasSubmittedContactSales(userId),
  );

  const { contactCustomerSupport, isLoading } = useContactCustomerSupport();

  const submitContactSales = () => {
    trackTrialExpiredContactSales(source);

    return contactCustomerSupport({
      supportReason: REAMS_SUPPORT_REASON.trialExpiredContactSales,
    })
      .then(() => {
        markSubmitted(userId);
        setIsSubmitted(true);
      })
      .catch(() => {
        notification.error({
          title: tCommon('supportRequestFailedErrorMessage'),
          description: t('supportRequestFailedErrorDescription'),
          placement: 'top',
        });
      });
  };

  return { isSubmitted, isLoading, submitContactSales };
};
